import { gql, type TypedDocumentNode } from '@apollo/client';
import { Navigate } from 'react-router';

import {
  ANY_SPORT_PLAY,
  ANY_SPORT_PLAY_BOARDS_PRO_BOARD,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useEnableSorare27Nav } from '@sorare/core/src/hooks/useEnableSorare27Nav.ts';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { useLastVisitedBoardView } from '#src/hooks/useLastVisitedBoardView.ts';

import type {
  ProCareerPageQuery,
  ProCareerPageQueryVariables,
} from './__generated__/page.graphql.ts';

// oxlint-disable-next-line sorare/no-unnormalized-object-in-array -- pre-existing, unrelated to this change
const PRO_CAREER_PAGE_QUERY = gql`
  query ProCareerPageQuery($sport: Sport!) {
    currentUser {
      slug
      boards(
        mode: CAREER
        sport: $sport
        rarities: [limited, rare, super_rare, unique]
      ) {
        id
        title
      }
    }
  }
` as TypedDocumentNode<ProCareerPageQuery, ProCareerPageQueryVariables>;

export const loader = queryLoader(PRO_CAREER_PAGE_QUERY, ({ sport }) => ({
  sport,
}));

const ProCareerPage = () => {
  const { generateSportContextPath, sport } = useSportContext();
  const enableSorare27Nav = useEnableSorare27Nav();
  const {
    flags: {
      enableProCareerMode = {
        baseball: false,
        football: false,
        nba: false,
      },
    },
  } = useFeatureFlags();
  const { data } = useQueryLoader<ProCareerPageQuery>();
  const { getProView } = useLastVisitedBoardView();

  if (!enableProCareerMode[sport.toLowerCase()] && !enableSorare27Nav) {
    return <Navigate to={generateSportContextPath(ANY_SPORT_PLAY)} replace />;
  }

  const boards = data?.currentUser?.boards ?? [];
  const lastBoardTitle = getProView().lastVisitedBoardTitle;
  const targetBoard = lastBoardTitle
    ? boards.find(b => b.title === lastBoardTitle)
    : null;
  const boardToNavigate = targetBoard ?? boards[0];

  if (boardToNavigate) {
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_PLAY_BOARDS_PRO_BOARD, {
          params: { board: idFromObject(boardToNavigate.id) },
        })}
        replace
      />
    );
  }

  return null;
};
export default ProCareerPage;
